import { FormEvent } from "react";
import { Link } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import googleSignInImage from "../../../assets/images/auth_page/web_neutral_sq_SI.svg";
import googleSignUpImage from "../../../assets/images/auth_page/web_neutral_sq_SU.svg";
import MobilePasswordField from "./MobilePasswordField";
import HoneypotField from "../../../components/CommonComponents/HoneypotField/HoneypotField";
import { useAuthPage } from "../useAuthPage";

type MobileAuthFormProps = {
    auth: ReturnType<typeof useAuthPage>;
    isLogin: boolean;
    onSwitchScreen: () => void;
};

const MobileAuthForm = ({ auth, isLogin, onSwitchScreen }: MobileAuthFormProps) => {
    const currentError = isLogin ? auth.loginErrorMessage : auth.registerErrorMessage;
    const isSubmitting = isLogin ? auth.isLoggingIn : auth.isSubmittingRegistration;

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isLogin) auth.handleLogin();
        else auth.validateRegistrationForm();
    };

    return (
        <div className="mobile-auth-form-transition">
            <h2>{isLogin ? "Login" : "Create Account"}</h2>
            <form onSubmit={handleSubmit} noValidate>
                <HoneypotField
                    value={isLogin ? auth.loginHoneypot : auth.registerHoneypot}
                    onChange={(event) => isLogin
                        ? auth.setLoginHoneypot(event.target.value)
                        : auth.setRegisterHoneypot(event.target.value)}
                />
                {!isLogin && (
                    <label className="mobile-auth-field">
                        <span className="sr-only">Full name</span>
                        <input
                            type="text"
                            placeholder="Full name"
                            autoComplete="name"
                            value={auth.name}
                            onChange={(event) => auth.setName(event.target.value)}
                        />
                    </label>
                )}

                <label className="mobile-auth-field">
                    <span className="sr-only">Email</span>
                    <input
                        type="email"
                        placeholder="Email"
                        autoComplete="email"
                        inputMode="email"
                        value={isLogin ? auth.loginEmail : auth.registerEmail}
                        onChange={(event) => isLogin
                            ? auth.setLoginEmail(event.target.value)
                            : auth.setRegisterEmail(event.target.value)}
                    />
                </label>

                <MobilePasswordField
                    value={isLogin ? auth.loginPassword : auth.registerPassword}
                    placeholder="Password"
                    visible={isLogin ? auth.showLoginPassword : auth.showRegisterPassword}
                    onChange={isLogin ? auth.setLoginPassword : auth.setRegisterPassword}
                    onToggle={isLogin ? auth.toggleLoginPasswordVisibility : auth.toggleRegisterPasswordVisibility}
                    autoComplete={isLogin ? "current-password" : "new-password"}
                />

                {!isLogin && (
                    <MobilePasswordField
                        value={auth.confirmPassword}
                        placeholder="Confirm password"
                        visible={auth.showConfirmPassword}
                        onChange={auth.setConfirmPassword}
                        onToggle={auth.toggleConfirmPasswordVisibility}
                        autoComplete="new-password"
                    />
                )}

                <p className="mobile-auth-error" aria-live="polite">{currentError}</p>

                {isLogin && (
                    <button
                        type="button"
                        className="mobile-auth-text-button"
                        onClick={() => auth.setShowResetPasswordModal(true)}
                    >
                        Forgot your password?
                    </button>
                )}

                <button type="submit" className="mobile-auth-primary-button" disabled={isSubmitting}>
                    {isSubmitting
                        ? <CircularProgress size={18} thickness={5} sx={{ color: "#fff" }} />
                        : isLogin ? "Login" : "Register"}
                </button>

                <p className="mobile-auth-switch">
                    {isLogin ? "Don't have an account yet?" : "Already have an account?"}
                    <button type="button" onClick={onSwitchScreen}>
                        {isLogin ? "Sign Up" : "Login"}
                    </button>
                </p>

                <div className="mobile-auth-divider"><span>Or</span></div>
                <button type="button" className="mobile-auth-google" onClick={auth.handleGoogleAuth}>
                    <img src={isLogin ? googleSignInImage : googleSignUpImage} alt={isLogin ? "Sign in with Google" : "Sign up with Google"} />
                </button>

                <p className="mobile-auth-legal">
                    By {isLogin ? "logging in" : "signing up"}, you agree to the{" "}
                    <Link to="/terms-of-service">Terms and Conditions</Link> and the{" "}
                    <Link to="/privacy-policy">Privacy Policy</Link>
                </p>
            </form>
        </div>
    );
};

export default MobileAuthForm;
